import dayjs from "dayjs";
import { LogeEvent, LogeMember, Participant } from "../types/entities";
import { VoteAnswer } from "./events-api";

type HostId = LogeEvent["host"];

/**
 * Who an event's vote is actually asking: the loge minus the host.
 *
 * Whoever calls a samling is hosting it, so there is nothing for them to
 * answer. Counting them made a full turnout read as one short — "6 af 7 har
 * stemt" when everyone who could had.
 */
export const votersFor = (members: LogeMember[], hostId: HostId) =>
  members.filter((m) => m.id !== hostId);

/** Votes from anyone but the host, who isn't asked and shouldn't be tallied. */
export const votesFromVoters = <T extends { user_id: number }>(votes: T[], hostId: HostId) =>
  votes.filter((v) => v.user_id !== hostId);

/**
 * Who is coming, host first: they're attending their own samling by default,
 * whether or not there's a vote of theirs on record. But an admin can record
 * an explicit answer for the host too (the confirmed samling's attendance
 * editor) — and once one exists, it decides the host's place in this list
 * exactly like anyone else's, rather than the default winning forever.
 */
export const attendingIds = (participants: Participant[], hostId: HostId) => {
  const saidYes = participants
    .filter((p) => p.answer === VoteAnswer.YES)
    .map((p) => p.user_id);
  if (hostId == null) return saidYes;
  const hostAnswered = participants.some((p) => p.user_id === hostId);
  return hostAnswered ? saidYes : [hostId, ...saidYes];
};

/**
 * Who said maybe — the host included, once they have an explicit maybe on
 * record. Their default (attending, with nothing recorded) is handled in
 * attendingIds above, not here.
 */
export const maybeIds = (participants: Participant[], hostId: HostId) =>
  participants.filter((p) => p.answer === VoteAnswer.MAYBE).map((p) => p.user_id);

/**
 * Which date_option a confirmed event's attendance should be edited against,
 * if any. Closing a vote sets loge_events.date to the winning option's own
 * date (see useCloseEventWithWinner), so the option matching that date *is*
 * the confirmed date — this is what makes it possible to edit attendance as
 * a vote on that one specific date, rather than the fuzzier "said yes to ANY
 * date this event ever had" that loge_event_participants aggregates before
 * one has been settled on.
 *
 * Comes back undefined for an event confirmed by hand-editing its date
 * outside the vote flow, with no matching date_option ever created —
 * attendance isn't editable there without first knowing which vote row would
 * even represent it.
 */
export const confirmedDateOptionId = (
  dateOptions: Array<{ id: number; date: string }>,
  eventDate: string | null | undefined,
): number | undefined => {
  if (!eventDate) return undefined;
  return dateOptions.find((d) => dayjs(d.date).isSame(eventDate, "day"))?.id;
};
